import { messages } from "./locales";
import { injectSeo } from "./seo";

export var SUPPORTED_LANGS = ["pl", "en", "de", "uk"];
var STORAGE_KEY = "ugrzejmnie_lang";
var LANG_LABELS = { pl: "PL", en: "EN", de: "DE", uk: "UK" };

var locale = "pl";
var listeners = [];

function lookup(dict, key) {
  if (!dict || !key) return null;
  var parts = key.split(".");
  var node = dict;
  for (var i = 0; i < parts.length; i += 1) {
    if (node == null || typeof node !== "object") return null;
    node = node[parts[i]];
  }
  return typeof node === "string" ? node : null;
}

export function t(key, vars) {
  var value = lookup(messages[locale], key) || lookup(messages.pl, key) || key;
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, function (_, name) {
    return vars[name] != null ? String(vars[name]) : "";
  });
}

export function getLocale() {
  return locale;
}

export function onLocaleChange(fn) {
  listeners.push(fn);
  return function () {
    listeners = listeners.filter(function (listener) {
      return listener !== fn;
    });
  };
}

// Localized pages live under /<lang>/ subdirectories (pre-translated static
// files — see scripts/build-locales.mjs). The ?lang= parameter is still
// honoured for legacy links, but the canonical form is the path prefix.
function pathLocale() {
  var m = (window.location.pathname || "/").match(/^\/(en|de|uk)(?=\/|$)/);
  return m ? m[1] : null;
}

function detectLocale() {
  try {
    var fromPath = pathLocale();
    if (fromPath) return fromPath;

    var params = new URLSearchParams(window.location.search);
    var queryLang = (params.get("lang") || "").toLowerCase();
    if (SUPPORTED_LANGS.indexOf(queryLang) !== -1) return queryLang;

    var stored = (localStorage.getItem(STORAGE_KEY) || "").toLowerCase();
    if (SUPPORTED_LANGS.indexOf(stored) !== -1) return stored;
  } catch (err) {
    /* ignore */
  }

  var nav = ((navigator.languages && navigator.languages[0]) || navigator.language || "pl").toLowerCase();
  if (nav.indexOf("uk") === 0) return "uk";
  if (nav.indexOf("de") === 0) return "de";
  if (nav.indexOf("en") === 0) return "en";
  return "pl";
}

function persistLocale(next) {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch (err) {
    /* ignore */
  }
}

// URL of the current page in a given locale — adds or strips the /<lang>/
// prefix, keeping the rest of the path and the anchor.
function localizedPath(next) {
  var path = (window.location.pathname || "/").replace(/^\/(en|de|uk)(?=\/|$)/, "") || "/";
  if (next === "pl") return path + window.location.hash;
  return "/" + next + (path === "/" ? "/" : path) + window.location.hash;
}

function applyMeta() {
  var path = (window.location.pathname || "/").replace(/\/index\.html$/, "/");
  if (path !== "/") return;
  // Pages may declare their own meta key prefix (e.g. SENTBeton uses
  // <body data-i18n-meta="sb.meta">); the UgrzejMnie home page defaults to "meta".
  var prefix = "meta";
  if (document.body && document.body.getAttribute("data-i18n-meta")) {
    prefix = document.body.getAttribute("data-i18n-meta");
  }
  document.title = t(prefix + ".title");
  var description = document.querySelector('meta[name="description"]');
  if (description) description.setAttribute("content", t(prefix + ".description"));
  var ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute("content", t(prefix + ".ogTitle"));
  var ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) ogDescription.setAttribute("content", t(prefix + ".ogDescription"));
}

export function applyTranslations(root) {
  var scope = root || document;

  scope.querySelectorAll("[data-i18n]").forEach(function (el) {
    el.textContent = t(el.getAttribute("data-i18n"));
  });

  scope.querySelectorAll("[data-i18n-html]").forEach(function (el) {
    el.innerHTML = t(el.getAttribute("data-i18n-html"));
  });

  scope.querySelectorAll("[data-i18n-placeholder]").forEach(function (el) {
    el.setAttribute("placeholder", t(el.getAttribute("data-i18n-placeholder")));
  });

  scope.querySelectorAll("[data-i18n-aria-label]").forEach(function (el) {
    el.setAttribute("aria-label", t(el.getAttribute("data-i18n-aria-label")));
  });

  scope.querySelectorAll("[data-i18n-alt]").forEach(function (el) {
    el.setAttribute("alt", t(el.getAttribute("data-i18n-alt")));
  });

  applyMeta();
  // Pages with their own OG/canonical/JSON-LD (e.g. SENTBeton) opt out of the
  // UgrzejMnie-specific SEO injection via data-i18n-seo="off".
  if (!(document.body && document.body.getAttribute("data-i18n-seo") === "off")) {
    injectSeo(locale);
  }
}

export function setLocale(next, options) {
  if (SUPPORTED_LANGS.indexOf(next) === -1) {
    applyTranslations();
    return;
  }
  // Locale switch = real navigation to the localized file under /<lang>/,
  // so the URL is always canonical and shareable.
  if (next !== locale) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      /* ignore */
    }
    window.location.assign(localizedPath(next));
    return;
  }

  locale = next;
  document.documentElement.lang = next;
  if (!options || options.persist !== false) {
    persistLocale(next);
  }
  applyTranslations();
  syncLangSwitch();
  listeners.forEach(function (fn) {
    fn(next);
  });
}

function syncLangSwitch() {
  document.querySelectorAll("[data-lang-switch]").forEach(function (root) {
    var current = root.querySelector("[data-lang-current]");
    if (current) current.textContent = LANG_LABELS[locale] || locale.toUpperCase();
    root.querySelectorAll("[data-lang]").forEach(function (btn) {
      var active = btn.getAttribute("data-lang") === locale;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
    });
  });
}

function closeLangSwitch(root) {
  if (!root) return;
  root.classList.remove("is-open");
  var toggle = root.querySelector("[data-lang-toggle]");
  if (toggle) toggle.setAttribute("aria-expanded", "false");
}

function initLangSwitch() {
  document.querySelectorAll("[data-lang-switch]").forEach(function (root) {
    var toggle = root.querySelector("[data-lang-toggle]");
    if (toggle) {
      toggle.addEventListener("click", function (event) {
        event.stopPropagation();
        var willOpen = !root.classList.contains("is-open");
        document.querySelectorAll("[data-lang-switch].is-open").forEach(function (openRoot) {
          if (openRoot !== root) closeLangSwitch(openRoot);
        });
        root.classList.toggle("is-open", willOpen);
        toggle.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });
    }

    root.querySelectorAll("[data-lang]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        setLocale(btn.getAttribute("data-lang"));
        closeLangSwitch(root);
      });
    });
  });

  document.addEventListener("click", function (event) {
    document.querySelectorAll("[data-lang-switch].is-open").forEach(function (root) {
      if (!root.contains(event.target)) closeLangSwitch(root);
    });
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") {
      document.querySelectorAll("[data-lang-switch].is-open").forEach(closeLangSwitch);
    }
  });
}

export function initI18n() {
  locale = detectLocale();
  document.documentElement.lang = locale;
  persistLocale(locale);
  applyTranslations();
  syncLangSwitch();
  initLangSwitch();
}
